/**
 * Яндекс.Метрика ЛК.
 *
 * Два счётчика:
 * - `110508890` — счётчик продукта (ЛК): просмотры всех разделов и JS-цели воронки
 *   «проценка → результаты → корзина → заказ», а также успешная регистрация;
 * - `109862254` — счётчик сайта eplex.ru: стоит на маркетинговом сайте, здесь нужен
 *   для сквозной воронки «сайт → регистрация в ЛК» (просмотры и цель регистрации).
 *
 * Загрузчик tag.js подключается один раз на оба счётчика — так Яндекс рекомендует
 * ставить несколько счётчиков на одну страницу. Оба инициализируются с `defer: true`:
 * автоматический первый просмотр выключен, и просмотр каждого маршрута отправляется
 * вручную из `router.afterEach` (`trackPageView`). Иначе SPA отдавала бы один просмотр
 * на всю сессию, а при ручных хитах — ещё и дубль первого.
 *
 * Гейт по хосту: счётчики работают только на lk.eplex.ru. Стенды, preview и localhost
 * в статистику не пишут. Проверка на проде — `?_ym_debug=2` в адресе: в консоли видны
 * `hit` и `reachGoal`, в Network — запросы `mc.yandex.ru/watch/<id>` на каждый переход.
 */
export const METRIKA_PRODUCT_COUNTER_ID = 110508890;
export const METRIKA_SITE_COUNTER_ID = 109862254;

/** JS-цели, заведённые в счётчиках. Идентификаторы согласованы с маркетингом. */
export type MetrikaGoal =
  | "pricing_search_started"
  | "pricing_results_received"
  | "cart_item_added"
  | "order_placed"
  | "registration_success";

const REGISTRATION_GOAL: MetrikaGoal = "registration_success";
const GOAL_SENT_STORAGE_KEY = "eplex_registration_goal_sent";
const PROD_HOSTNAME = "lk.eplex.ru";
const METRIKA_TAG_SRC = `https://mc.yandex.ru/metrika/tag.js?id=${METRIKA_PRODUCT_COUNTER_ID}`;
const ALL_COUNTERS = [METRIKA_PRODUCT_COUNTER_ID, METRIKA_SITE_COUNTER_ID] as const;

interface YmFunction {
  (...args: unknown[]): void;
  a?: unknown[][];
  l?: number;
}

declare global {
  interface Window {
    ym?: YmFunction;
    dataLayer?: unknown[];
    __eplexMetrikaInitialized?: boolean;
  }
}

let lastTrackedUrl: string | null = null;

function isMetrikaEnabled(): boolean {
  return window.location.hostname === PROD_HOSTNAME;
}

// Очередь вызовов до загрузки tag.js — тот же приём, что в официальном коде счётчика:
// tag.js после загрузки проигрывает `ym.a`, поэтому init/hit/reachGoal не теряются
function ensureYmStub(): YmFunction {
  if (!window.ym) {
    const ym: YmFunction = (...args: unknown[]) => {
      ym.a = ym.a ?? [];
      ym.a.push(args);
    };
    ym.l = Date.now();
    window.ym = ym;
  }
  return window.ym;
}

function appendMetrikaScript(): void {
  const alreadyLoaded = Array.from(document.scripts).some((script) => script.src === METRIKA_TAG_SRC);
  if (alreadyLoaded) return;

  const script = document.createElement("script");
  script.async = true;
  script.src = METRIKA_TAG_SRC;
  document.head.appendChild(script);
}

function getYm(): YmFunction | null {
  return typeof window.ym === "function" ? window.ym : null;
}

/**
 * Подключение счётчиков: вызывается на первой навигации роутера, повторные вызовы
 * игнорируются. Ленивый init вместо кода в `<head>` — чтобы гейт по хосту и порядок
 * «init → первый hit» жили в одном месте, а не расходились между index.html и кодом.
 */
export function initMetrika(): void {
  if (!isMetrikaEnabled() || window.__eplexMetrikaInitialized) return;
  window.__eplexMetrikaInitialized = true;

  try {
    window.dataLayer = window.dataLayer ?? [];
    const ym = ensureYmStub();
    appendMetrikaScript();
    for (const counterId of ALL_COUNTERS) {
      ym(counterId, "init", {
        defer: true,
        ssr: true,
        webvisor: true,
        clickmap: true,
        ecommerce: "dataLayer",
        accurateTrackBounce: true,
        trackLinks: true,
      });
    }
  } catch (error) {
    console.error("Metrika init error: ", error);
  }
}

/**
 * Виртуальный просмотр текущего адреса в обоих счётчиках — на каждый переход роутера,
 * включая первый (из-за `defer: true`). `referer` — предыдущий просмотренный адрес,
 * чтобы в отчётах Метрики переходы между разделами складывались в путь по сайту.
 */
export function trackPageView(): void {
  if (!isMetrikaEnabled()) return;

  try {
    const ym = getYm();
    if (!ym) return;
    const currentUrl = window.location.href;
    const options = { title: document.title, referer: lastTrackedUrl ?? document.referrer };
    for (const counterId of ALL_COUNTERS) {
      ym(counterId, "hit", currentUrl, options);
    }
    lastTrackedUrl = currentUrl;
  } catch (error) {
    console.error("Metrika hit error: ", error);
  }
}

/** JS-цель продуктовой воронки в счётчике ЛК. Условия достижения — в `metrikaGoals.ts`. */
export function sendMetrikaGoal(goal: MetrikaGoal): void {
  if (!isMetrikaEnabled()) return;

  try {
    getYm()?.(METRIKA_PRODUCT_COUNTER_ID, "reachGoal", goal);
  } catch (error) {
    console.error("Metrika goal error: ", error);
  }
}

/**
 * Цель «Успешная регистрация» в обоих счётчиках: в счётчике ЛК — как шаг продуктовой
 * воронки, в счётчике сайта — как завершение сквозной воронки «сайт → аккаунт».
 * Вызывается только после успешного ответа API. Guard в sessionStorage защищает от
 * повторного достижения в рамках сессии.
 */
export function sendRegistrationGoal(): void {
  if (!isMetrikaEnabled()) return;

  try {
    if (sessionStorage.getItem(GOAL_SENT_STORAGE_KEY) === "1") return;
    const ym = getYm();
    if (!ym) return;
    for (const counterId of ALL_COUNTERS) {
      ym(counterId, "reachGoal", REGISTRATION_GOAL);
    }
    sessionStorage.setItem(GOAL_SENT_STORAGE_KEY, "1");
  } catch (error) {
    console.error("Metrika goal error: ", error);
  }
}
